/*
 * SPDX-License-Identifier: Apache-2.0
 *
 * The OpenSearch Contributors require contributions made to
 * this file be licensed under the Apache-2.0 license or a
 * compatible open source license.
 *
 * Modifications Copyright OpenSearch Contributors. See
 * GitHub history for details.
 */

/*
 * Licensed to Elasticsearch B.V. under one or more contributor
 * license agreements. See the NOTICE file distributed with
 * this work for additional information regarding copyright
 * ownership. Elasticsearch B.V. licenses this file to you under
 * the Apache License, Version 2.0 (the "License"); you may
 * not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *    http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import React, { useEffect } from 'react';
import PropTypes from "prop-types";
import classNames from 'classnames';
import { OuiHeaderSectionItem, OuiHeaderSection } from './header_section';
import { OuiHeaderBreadcrumbs } from './header_breadcrumbs';
function createHeaderSection(sections, border) {
  return sections.map(function (section, index) {
    return /*#__PURE__*/React.createElement(OuiHeaderSectionItem, {
      key: index,
      border: border
    }, section);
  });
}
// Start a counter to manage the total number of fixed headers that need the body class
var ouiHeaderFixedCounter = 0;
export var OuiHeader = ({
  children,
  className,
  sections,
  position = 'static',
  theme = 'default',
  ...rest
}) => {
  const classes = classNames('ouiHeader', `ouiHeader--${theme}`, `ouiHeader--${position}`, className);
  useEffect(() => {
    if (position === 'fixed') {
      // Increment fixed header counter for each fixed header
      ouiHeaderFixedCounter++;
      document.body.classList.add('ouiBody--headerIsFixed');
      return () => {
        // Both decrement the fixed counter AND then check if there are none
        if (--ouiHeaderFixedCounter === 0) {
          // If there are none, THEN remove class
          document.body.classList.remove('ouiBody--headerIsFixed');
        }
      };
    }
  }, [position]);
  let contents;
  if (sections) {
    if (children) {
      // In case both children and sections are passed, warn in the console that the children will be disregarded
      console.warn('OuiHeader cannot accept both `children` and `sections`. It will disregard the `children`.');
    }
    contents = sections.map((section, index) => {
      const content = [];
      if (section.items) {
        // Items get wrapped in OuiHeaderSection and each item in a OuiHeaderSectionItem
        content.push(<OuiHeaderSection key={`items-${index}`}>
            {createHeaderSection(section.items, section.borders)}
          </OuiHeaderSection>);
      }
      if (section.breadcrumbs) {
        content.push(
        // Breadcrumbs are separate and cannot be contained in a OuiHeaderSection
        // in order for truncation to work
        <OuiHeaderBreadcrumbs key={`breadcrumbs-${index}`} breadcrumbs={section.breadcrumbs} {...section.breadcrumbProps} />);
      }
      return content;
    });
  } else {
    contents = children;
  }
  return <div className={classes} {...rest}>
      {contents}
    </div>;
};
OuiHeader.propTypes = {
  className: PropTypes.string,
  "aria-label": PropTypes.string,
  "data-test-subj": PropTypes.string,
  /**
       * An array of objects to wrap in a #OuiHeaderSection.
       * Each section is spaced using `space-between`.
       * See #OuiHeaderSectionsProp for object details.
       * This prop disregards the prop `children` if both are passed.
       */
  sections: PropTypes.arrayOf(PropTypes.shape({
    /**
       * An arry of items that will be wrapped in a #OuiHeaderSectionItem
       */
    items: PropTypes.arrayOf(PropTypes.node.isRequired),
    /**
       * Apply the passed border side to each #OuiHeaderSectionItem
       */
    borders: PropTypes.oneOf(["left", "right", "none"]),
    /**
       * Breadcrumbs in the header cannot be wrapped in a #OuiHeaderSection in order for truncation to work.
       * Simply pass the array of OuiBreadcrumb objects
       */
    breadcrumbs: PropTypes.arrayOf(PropTypes.shape({
      className: PropTypes.string,
      "aria-label": PropTypes.string,
      "data-test-subj": PropTypes.string,
      /**
         * Visible label of the breadcrumb
         */
      text: PropTypes.node.isRequired,
      href: PropTypes.string,
      onClick: PropTypes.func,
      /**
         * Force a max-width on the breadcrumb text
         */
      truncate: PropTypes.bool
    }).isRequired),
    /**
       * Other props to pass to #OuiHeaderBreadcrumbs
       */
    breadcrumbProps: PropTypes.any
  }).isRequired),
  /**
       * Helper that positions the header against the window body and
       * adds the correct amount of top padding to the window when in `fixed` mode
       */
  position: PropTypes.oneOf(["static", "fixed"]),
  /**
       * The `default` will inherit its coloring from the light or dark theme.
       * Or, force the header into pseudo `dark` theme for all themes.
       */
  theme: PropTypes.oneOf(["default", "dark"])
};
try {
  OuiHeader.__docgenInfo = {
    tags: {},
    filePath: '/codebuild/output/src2210237963/src/src/components/header/header.tsx',
    description: '',
    displayName: 'OuiHeader',
    methods: [],
    props: {
      className: {
        defaultValue: null,
        description: '',
        name: 'className',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'HTMLAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'aria-label': {
        defaultValue: null,
        description: 'Defines a string value that labels the current element.\n' + '@see aria-labelledby.',
        name: 'aria-label',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }, {
          fileName: 'src/node_modules/@types/react/index.d.ts',
          name: 'AriaAttributes'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      'data-test-subj': {
        defaultValue: null,
        description: '',
        name: 'data-test-subj',
        parent: {
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        },
        declarations: [{
          fileName: 'src/src/components/common.ts',
          name: 'CommonProps'
        }],
        required: false,
        type: {
          name: 'string'
        }
      },
      sections: {
        defaultValue: null,
        description: 'An array of objects to wrap in a #OuiHeaderSection.\n' + 'Each section is spaced using `space-between`.\n' + 'See #OuiHeaderSectionsProp for object details.\n' + 'This prop disregards the prop `children` if both are passed.',
        name: 'sections',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/header/header.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'OuiHeaderSections[]'
        }
      },
      position: {
        defaultValue: {
          value: 'static'
        },
        description: 'Helper that positions the header against the window body and\n' + 'adds the correct amount of top padding to the window when in `fixed` mode',
        name: 'position',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/header/header.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"fixed" | "static"',
          value: [{
            value: '"fixed"'
          }, {
            value: '"static"'
          }]
        }
      },
      theme: {
        defaultValue: {
          value: 'default'
        },
        description: 'The `default` will inherit its coloring from the light or dark theme.\n' + 'Or, force the header into pseudo `dark` theme for all themes.',
        name: 'theme',
        parent: undefined,
        declarations: [{
          fileName: 'src/src/components/header/header.tsx',
          name: 'TypeLiteral'
        }],
        required: false,
        type: {
          name: 'enum',
          raw: '"default" | "dark"',
          value: [{
            value: '"default"'
          }, {
            value: '"dark"'
          }]
        }
      }
    },
    extendedInterfaces: ['CommonProps', 'HTMLAttributes', 'AriaAttributes', 'DOMAttributes']
  };
} catch (e) {}